import { keepPreviousData, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query'
import { fetchGalleries } from '../api/galleryApi'
import type { GalleryQueryParams, GalleryListItem } from '../types'

type GalleriesQueryData = {
  galleries: GalleryListItem[]
  totalCount: number
  page: number
  pageSize: number
}

type GalleriesQueryResult = UseQueryResult<GalleriesQueryData> & {
  totalPages: number
  hasNextPage: boolean
  hasPrevPage: boolean
  prefetchPage: (page: number) => Promise<unknown>
}

/**
 * useGalleries — paginated list hook
 *
 * TODOs:
 * - [ ] Accept URL param object (parsed in page) and memoize stable key
 * - [ ] Expose helpers: totalPages, hasNext/Prev based on totalCount
 * - [ ] Add prefetch on next/prev page to improve UX
 * - [ ] Optionally expose `setParams` when used inside a provider
 * - [ ] Placeholder data/skeleton alignment for first load vs pagination
 */

export function useGalleries(params: GalleryQueryParams): GalleriesQueryResult {
  const qc = useQueryClient()
  const query = useQuery<GalleriesQueryData>({
    queryKey: ['galleries', params],
    queryFn: () => fetchGalleries(params),
    placeholderData: keepPreviousData,
    staleTime: 60_000
  })

  const totalPages = query.data ? Math.max(1, Math.ceil(query.data.totalCount / query.data.pageSize)) : 1
  const hasNextPage = query.data ? query.data.page < totalPages : false
  const hasPrevPage = query.data ? query.data.page > 1 : false

  const prefetchPage = (page: number) => {
    const nextParams = { ...params, page }
    return qc.prefetchQuery({ queryKey: ['galleries', nextParams], queryFn: () => fetchGalleries(nextParams) })
  }

  return { ...query, totalPages, hasNextPage, hasPrevPage, prefetchPage }
}
